"use client";

import { useEffect, useMemo, useSyncExternalStore } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/Badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/Textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/Form";
import { LoadingSkeleton } from "@/components/loading-skeleton";
import { formatShopLocation } from "@/features/shop/shop-location";
import {
  formatPaymentMethodLabel,
  formatProductUnitLabel,
} from "@/features/shop/types";
import {
  isPopulatedCartProduct,
  type CustomerCart,
  type CustomerProduct,
} from "@/features/shop/types/customer-shop";
import {
  useCheckoutCustomerOrder,
  useClearCustomerCart,
  useRemoveCustomerCartItem,
  useSetCustomerCartItemQuantity,
  useSyncLocalCustomerCart,
} from "@/features/shop/customer/customer-shop.mutations";
import {
  useCustomerCart,
  useCustomerShop,
} from "@/features/shop/customer/customer-shop.queries";
import {
  clearLocalCustomerCart,
  getLocalCustomerCart,
  removeLocalCustomerCartItem,
  subscribeLocalCustomerCart,
} from "@/features/shop/cart/customer-cart-storage";
import { CartQuantityStepper } from "@/components/blocks/CartQuantityStepper";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  customerCheckoutSchema,
  type CustomerCheckoutFormValues,
} from "@/features/shop/customer/customer-shop.schema";
import { z } from "zod";
import { ArrowLeft, Loader2, LogIn, Trash2 } from "lucide-react";

function isPopulatedShop(shop: CustomerCart["shopId"] | undefined) {
  return !!shop && typeof shop === "object" && "_id" in shop;
}

type CartLineItem = {
  productId: string;
  product: CustomerProduct | null;
  quantity: number;
};

export default function ShopCartPage() {
  const router = useRouter();
  const { userAccount, loading } = useAuth();
  const isCustomer = !loading && userAccount?.role === "customer";
  const isSignedInNonCustomer =
    !loading && !!userAccount && userAccount.role !== "customer";
  const localCart = useSyncExternalStore(
    subscribeLocalCustomerCart,
    getLocalCustomerCart,
    () => null,
  );
  const clearCart = useClearCustomerCart();
  const setCartItemQuantity = useSetCustomerCartItemQuantity();
  const removeCartItem = useRemoveCustomerCartItem();
  const checkout = useCheckoutCustomerOrder();
  const syncLocalCart = useSyncLocalCustomerCart();

  const hasLocalCart = Boolean(localCart?.items.length);
  const shouldUseServerCart = isCustomer && !hasLocalCart;

  const {
    data: serverCartResponse,
    isLoading: isLoadingServerCart,
    error: serverCartError,
  } = useCustomerCart({ enabled: shouldUseServerCart });
  const {
    data: localShopResponse,
    isLoading: isLoadingLocalShop,
    error: localShopError,
  } = useCustomerShop(localCart?.shopId, undefined);

  const serverCart = serverCartResponse?.data ?? null;
  const localShop = localShopResponse?.data?.shop ?? null;
  const localProducts = useMemo(
    () => localShopResponse?.data?.products ?? [],
    [localShopResponse?.data?.products],
  );

  const usingLocalCart = hasLocalCart;
  const cartShop = usingLocalCart
    ? localShop
    : isPopulatedShop(serverCart?.shopId)
      ? serverCart.shopId
      : null;

  const cartLineItems = useMemo<CartLineItem[]>(() => {
    if (usingLocalCart) {
      const productMap = new Map(
        localProducts.map((product) => [product._id, product]),
      );
      return (localCart?.items ?? []).map((item) => ({
        productId: item.productId,
        product: productMap.get(item.productId) ?? null,
        quantity: item.quantity,
      }));
    }

    return (serverCart?.items ?? []).map((item) => {
      const product = isPopulatedCartProduct(item.productId)
        ? item.productId
        : null;
      return {
        productId: product?._id ?? String(item.productId ?? ""),
        product,
        quantity: item.quantity,
      };
    });
  }, [localCart?.items, localProducts, serverCart?.items, usingLocalCart]);

  const totalAmount = useMemo(() => {
    return cartLineItems.reduce((sum, item) => {
      return sum + (item.product?.price ?? 0) * item.quantity;
    }, 0);
  }, [cartLineItems]);

  const checkoutForm = useForm<
    z.input<typeof customerCheckoutSchema>,
    unknown,
    z.output<typeof customerCheckoutSchema>
  >({
    resolver: zodResolver(customerCheckoutSchema),
    defaultValues: {
      phone: "",
      deliveryLocation: "",
      paymentMethod: "cash_on_delivery",
    },
  });

  const { setValue } = checkoutForm;

  useEffect(() => {
    if (cartShop) {
      setValue("deliveryLocation", formatShopLocation(cartShop));
    }
  }, [cartShop, setValue]);

  function handleQuantityChange(productId: string, quantity: number) {
    setCartItemQuantity.mutate({ productId, quantity });
  }

  async function handleRemoveItem(productId: string) {
    if (usingLocalCart) {
      removeLocalCustomerCartItem(productId);
      toast.success("Item removed from cart.");
      return;
    }

    await removeCartItem.mutateAsync(productId);
    toast.success("Item removed from cart.");
  }

  async function handleClearCart() {
    if (usingLocalCart) {
      clearLocalCustomerCart();
      toast.success("Cart cleared.");
      return;
    }

    await clearCart.mutateAsync();
    toast.success("Cart cleared.");
  }

  async function onCheckout(values: CustomerCheckoutFormValues) {
    try {
      if (!isCustomer) {
        toast.info(
          isSignedInNonCustomer
            ? "This account can't place orders. Sign in with a customer account to checkout."
            : "Please sign in to place your order.",
        );
        router.push(`/login?redirect=${encodeURIComponent("/shop/cart")}`);
        return;
      }

      if (usingLocalCart) {
        await syncLocalCart.mutateAsync();
      }

      const order = await checkout.mutateAsync({
        phone: values.phone,
        deliveryLocation: values.deliveryLocation,
        paymentMethod: values.paymentMethod,
      });
      toast.success("Order placed successfully.");
      const orderId = order.data?._id;
      router.push(orderId ? `/shop/orders/${orderId}` : "/shop/orders");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Checkout failed");
    }
  }

  const isLoadingCart = usingLocalCart
    ? isLoadingLocalShop
    : isLoadingServerCart;
  const cartError = usingLocalCart ? localShopError : serverCartError;
  const cartIsEmpty = cartLineItems.length === 0;
  const canCheckout = isCustomer && !cartIsEmpty;
  const cartDescription = usingLocalCart
    ? "Sign in to place the order."
    : "Order now.";

  return (
    <div className="p-4 max-w-2xl lg:max-w-6xl mx-auto">
      <Button
        variant="ghost"
        onClick={() => router.back()}
        className="w-fit gap-2 mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </Button>

      <div className="grid animate-in gap-6 fade-in slide-in-from-bottom-4 duration-500 fill-mode-both lg:grid-cols-[1.1fr_0.9fr]">
        <Card className="border-white/10">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle>
                {usingLocalCart ? "Your Local Cart" : "Your Cart"}
              </CardTitle>
              <p className="text-sm text-muted-foreground">{cartDescription}</p>
            </div>
            <Link href="/shop" className="inline-flex">
              <Button variant="outline">Continue Shopping</Button>
            </Link>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoadingCart ? (
              <LoadingSkeleton
                variant="rows"
                count={3}
                itemClassName="border-white/10"
                showAvatar={false}
                showAction
              />
            ) : cartError ? (
              <div className="rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                {cartError.message}
              </div>
            ) : cartIsEmpty ? (
              <div className="py-16 text-center text-sm text-muted-foreground">
                Your cart is empty.
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {cartLineItems.map((item) => {
                    const isUpdatingQuantity =
                      setCartItemQuantity.isPending &&
                      setCartItemQuantity.variables?.productId ===
                        item.productId;
                    return (
                      <div
                        key={item.productId}
                        className="rounded-2xl border border-white/10 bg-card/80 p-4"
                      >
                        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                          <div className="space-y-1">
                            <h3 className="font-semibold">
                              {item.product?.name ?? "Removed product"}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {item.product ? (
                                <>
                                  KES {item.product.price} ·{" "}
                                  {formatProductUnitLabel(item.product.unit)}
                                </>
                              ) : (
                                "Product no longer available"
                              )}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {item.product &&
                              isPopulatedShop(item.product.shopId)
                                ? `${item.product.shopId.name} · ${formatShopLocation(item.product.shopId)}`
                                : cartShop
                                  ? `${cartShop.name} · ${formatShopLocation(cartShop)}`
                                  : null}
                            </p>
                          </div>

                          <div className="flex items-end gap-3">
                            <CartQuantityStepper
                              name={item.product?.name ?? "item"}
                              quantity={item.quantity}
                              maxQuantity={item.product?.quantity}
                              onChange={(quantity) =>
                                handleQuantityChange(item.productId, quantity)
                              }
                              disabled={isUpdatingQuantity}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              className="text-destructive hover:text-destructive"
                              onClick={() => handleRemoveItem(item.productId)}
                              disabled={removeCartItem.isPending}
                            >
                              {removeCartItem.isPending &&
                              removeCartItem.variables === item.productId ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClearCart}
                    disabled={clearCart.isPending}
                  >
                    {clearCart.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : null}
                    {clearCart.isPending ? "Clearing..." : "Clear Cart"}
                  </Button>
                  <Badge variant="secondary" className="px-3 py-1">
                    Total: KES {totalAmount}
                  </Badge>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="border-white/10">
          <CardHeader>
            <CardTitle>Checkout</CardTitle>
            <p className="text-sm text-muted-foreground">
              {isCustomer
                ? "Cash on delivery only for now."
                : isSignedInNonCustomer
                  ? "This account can't place orders."
                  : "Sign in to place your order."}
            </p>
          </CardHeader>
          <CardContent>
            {!isCustomer ? (
              <div className="space-y-4">
                <div className="rounded-xl border border-primary/20 bg-primary/10 px-4 py-3 text-sm text-primary">
                  {isSignedInNonCustomer
                    ? "This account isn't a customer account, so it can't place orders. Sign out and sign in with a customer account to checkout."
                    : "Sign in at checkout to place your order."}
                </div>
                <Button asChild className="w-full">
                  <Link href="/login?redirect=%2Fshop%2Fcart">
                    <LogIn className="mr-2 h-4 w-4" />
                    {isSignedInNonCustomer ? "Switch account" : "Sign in to checkout"}
                  </Link>
                </Button>
              </div>
            ) : (
              <>
                {usingLocalCart ? (
                  <div className="mb-4 rounded-xl border border-primary/20 bg-primary/10 px-4 py-3 text-sm text-primary">
                    Your cart will sync to your account on checkout.
                  </div>
                ) : null}

                <Form {...checkoutForm}>
                  <form
                    className="space-y-4"
                    onSubmit={checkoutForm.handleSubmit(onCheckout)}
                  >
                    <FormField
                      control={checkoutForm.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone number</FormLabel>
                          <FormControl>
                            <Input placeholder="+254..." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={checkoutForm.control}
                      name="deliveryLocation"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Delivery location</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Estate, road, landmark or address"
                              rows={4}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={checkoutForm.control}
                      name="paymentMethod"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Payment method</FormLabel>
                          <FormControl>
                            <Select
                              value={field.value}
                              onValueChange={field.onChange}
                            >
                              <SelectTrigger className="h-10 w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="cash_on_delivery">
                                  {formatPaymentMethodLabel("cash_on_delivery")}
                                </SelectItem>
                                <SelectItem value="mpesa">
                                  {formatPaymentMethodLabel("mpesa")} (coming
                                  soon)
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      className="w-full rounded"
                      disabled={
                        !canCheckout ||
                        checkout.isPending ||
                        syncLocalCart.isPending
                      }
                    >
                      {checkout.isPending || syncLocalCart.isPending
                        ? "Placing Order..."
                        : "Place Order"}
                    </Button>
                  </form>
                </Form>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
